import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { authService } from "../api/services/auth/authService";
import {
  AuthContextType,
  LoginRequest,
  User,
  Permission,
  UserSidebarResponse,
} from "../api/services/auth/type";
import { sidebarService } from "../api/services/sidebar/sidebarService";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [sidebarData, setSidebarData] = useState<UserSidebarResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        if (authService.isAuthenticated()) {
          const currentUser = authService.getCurrentUser();
          setUser(currentUser);

          // Fetch sidebar data
          try {
            const sidebar = await sidebarService.getMySidebar();
            setSidebarData(sidebar);
          } catch (error) {
            console.error("Failed to fetch sidebar data:", error);
            // Continue without sidebar data, user can still use the app
          }
        }
      } catch {
        authService.logout();
        setSidebarData(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginRequest): Promise<void> => {
    setIsLoading(true);
    try {
      const loginResponse = await authService.login(credentials);
      setUser(loginResponse.user);

      // Fetch sidebar data after successful login
      try {
        const sidebar = await sidebarService.getMySidebar();
        setSidebarData(sidebar);
      } catch (error) {
        console.error("Failed to fetch sidebar data:", error);
        // Continue without sidebar data, user can still use the app
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    authService.logout();
    setUser(null);
    setSidebarData(null);
  };

  const getUserPermissions = (): Permission[] => {
    if (!user?.assignedRoles) return [];
    return user.assignedRoles.flatMap((role) => role.permissions);
  };

  const hasPermission = (action: string, tableName: string): boolean => {
    if (!user) return false;
    const permissions = getUserPermissions();
    return permissions.some(
      (permission) =>
        permission.action === action && permission.tableName === tableName,
    );
  };

  const hasAnyPermission = (
    requiredPermissions: Array<{ action: string; tableName: string }>,
  ): boolean => {
    if (!user) return false;
    return requiredPermissions.some(({ action, tableName }) =>
      hasPermission(action, tableName),
    );
  };

  const value: AuthContextType = {
    user,
    sidebarData,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading,
    hasPermission,
    hasAnyPermission,
    getUserPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
